/**
 * Google Analytics Utility
 * 
 * Provides helper functions for tracking custom events and user interactions
 * throughout the application.
 */

// Type definitions for common event parameters
interface AnalyticsEventParams {
  [key: string]: string | number | boolean | undefined | unknown[] | Record<string, unknown>;
}

interface PageViewParams extends Record<string, unknown> {
  page_title?: string;
  page_location?: string;
  page_path?: string;
}

/**
 * Check if Google Analytics is loaded and available
 */
export const isAnalyticsAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag !== 'undefined';
};

/**
 * Track a custom event
 * 
 * @param eventName - Name of the event (e.g., 'task_completed', 'user_signup')
 * @param eventParams - Additional parameters for the event
 * 
 * @example
 * trackEvent('task_completed', {
 *   task_id: 'task123',
 *   task_type: 'compliance',
 *   location_id: 'loc456'
 * });
 */
export const trackEvent = (
  eventName: string,
  eventParams?: AnalyticsEventParams
): void => {
  if (!isAnalyticsAvailable()) {
    console.warn('Google Analytics not available');
    return;
  }

  window.gtag('event', eventName, eventParams);
};

/**
 * Track a page view
 * 
 * @param pagePath - The path of the page
 * @param pageTitle - Optional page title
 * 
 * @example
 * trackPageView('/tasks', 'Task Manager');
 */
export const trackPageView = (
  pagePath: string,
  pageTitle?: string
): void => {
  if (!isAnalyticsAvailable()) {
    return;
  }

  const params: PageViewParams = {
    page_path: pagePath,
  };

  if (pageTitle) {
    params.page_title = pageTitle;
  }

  window.gtag('config', 'G-V75RMM3KLL', params);
};

/**
 * Set user properties (user_id, organization_id, role, etc.)
 * These persist across sessions
 * 
 * @param userProperties - Object containing user properties
 * 
 * @example
 * setUserProperties({
 *   user_id: 'user123',
 *   organization_id: 'org456',
 *   user_role: 'admin',
 *   subscription_tier: 'pro'
 * });
 */
export const setUserProperties = (
  userProperties: AnalyticsEventParams
): void => {
  if (!isAnalyticsAvailable()) {
    return;
  }

  window.gtag('set', 'user_properties', userProperties);
};

/**
 * Set user ID for cross-device tracking
 * 
 * @param userId - Unique user identifier
 */
export const setUserId = (userId: string): void => {
  if (!isAnalyticsAvailable()) {
    return;
  }

  window.gtag('set', { user_id: userId });
};

/**
 * Clear user ID (e.g., on logout)
 */
export const clearUserId = (): void => {
  if (!isAnalyticsAvailable()) {
    return;
  }

  window.gtag('set', { user_id: null });
};

// ============================================
// Predefined Event Tracking Functions
// ============================================

/**
 * Track user authentication events
 */
export const trackAuth = {
  login: (method: 'email' | 'google' | 'sso' = 'email') => {
    trackEvent('login', { method });
  },
  signup: (method: 'email' | 'google' = 'email') => {
    trackEvent('sign_up', { method });
  },
  logout: () => {
    trackEvent('logout');
    clearUserId();
  },
  emailVerification: () => {
    trackEvent('email_verification_sent');
  },
  emailVerified: () => {
    trackEvent('email_verified');
  },
};

/**
 * Track task-related events
 */
export const trackTask = {
  created: (taskType?: string, locationId?: string) => {
    trackEvent('task_created', {
      task_type: taskType,
      location_id: locationId,
    });
  },
  completed: (taskId: string, taskType?: string, locationId?: string) => {
    trackEvent('task_completed', {
      task_id: taskId,
      task_type: taskType,
      location_id: locationId,
    });
  },
  viewed: (taskId: string) => {
    trackEvent('task_viewed', { task_id: taskId });
  },
  deleted: (taskId: string) => {
    trackEvent('task_deleted', { task_id: taskId });
  },
};

/**
 * Track asset-related events
 */
export const trackAsset = {
  created: (assetType?: string, locationId?: string) => {
    trackEvent('asset_created', {
      asset_type: assetType,
      location_id: locationId,
    });
  },
  viewed: (assetId: string, assetType?: string) => {
    trackEvent('asset_viewed', {
      asset_id: assetId,
      asset_type: assetType,
    });
  },
  updated: (assetId: string) => {
    trackEvent('asset_updated', { asset_id: assetId });
  },
  deleted: (assetId: string) => {
    trackEvent('asset_deleted', { asset_id: assetId });
  },
  qrScanned: (assetId: string) => {
    trackEvent('asset_qr_scanned', { asset_id: assetId });
  },
};

/**
 * Track checklist-related events
 */
export const trackChecklist = {
  started: (checklistId: string, assetId?: string) => {
    trackEvent('checklist_started', {
      checklist_id: checklistId,
      asset_id: assetId,
    });
  },
  completed: (checklistId: string, assetId?: string) => {
    trackEvent('checklist_completed', {
      checklist_id: checklistId,
      asset_id: assetId,
    });
  },
  paused: (checklistId: string) => {
    trackEvent('checklist_paused', { checklist_id: checklistId });
  },
  resumed: (checklistId: string) => {
    trackEvent('checklist_resumed', { checklist_id: checklistId });
  },
};

/**
 * Track form-related events
 */
export const trackForm = {
  started: (formId: string) => {
    trackEvent('form_started', { form_id: formId });
  },
  completed: (formId: string) => {
    trackEvent('form_completed', { form_id: formId });
  },
  abandoned: (formId: string, completionPercentage?: number) => {
    trackEvent('form_abandoned', {
      form_id: formId,
      completion_percentage: completionPercentage,
    });
  },
};

/**
 * Track subscription-related events
 */
export const trackSubscription = {
  planViewed: (planName: string) => {
    trackEvent('view_item', {
      item_name: planName,
      item_category: 'subscription',
    });
  },
  checkoutStarted: (planName: string, price?: number) => {
    trackEvent('begin_checkout', {
      item_name: planName,
      value: price,
      currency: 'USD',
    });
  },
  purchased: (planName: string, price: number, transactionId: string) => {
    trackEvent('purchase', {
      transaction_id: transactionId,
      value: price,
      currency: 'USD',
      items: [
        {
          item_name: planName,
          item_category: 'subscription',
          price: price,
          quantity: 1,
        },
      ],
    });
  },
  cancelled: (planName: string) => {
    trackEvent('subscription_cancelled', { plan_name: planName });
  },
  upgraded: (fromPlan: string, toPlan: string) => {
    trackEvent('subscription_upgraded', {
      from_plan: fromPlan,
      to_plan: toPlan,
    });
  },
};

/**
 * Track organization and location events
 */
export const trackOrganization = {
  created: (organizationId: string) => {
    trackEvent('organization_created', { organization_id: organizationId });
  },
  locationAdded: (locationId: string, organizationId: string) => {
    trackEvent('location_added', {
      location_id: locationId,
      organization_id: organizationId,
    });
  },
  userInvited: (organizationId: string, role: string) => {
    trackEvent('user_invited', {
      organization_id: organizationId,
      user_role: role,
    });
  },
};

/**
 * Track evidence vault events
 */
export const trackEvidence = {
  uploaded: (evidenceType: string, fileSize?: number) => {
    trackEvent('evidence_uploaded', {
      evidence_type: evidenceType,
      file_size: fileSize,
    });
  },
  viewed: (evidenceId: string) => {
    trackEvent('evidence_viewed', { evidence_id: evidenceId });
  },
  downloaded: (evidenceId: string) => {
    trackEvent('evidence_downloaded', { evidence_id: evidenceId });
  },
  deleted: (evidenceId: string) => {
    trackEvent('evidence_deleted', { evidence_id: evidenceId });
  },
};

/**
 * Track messaging events
 */
export const trackMessaging = {
  messageSent: (threadId: string, hasAttachment?: boolean) => {
    trackEvent('message_sent', {
      thread_id: threadId,
      has_attachment: hasAttachment,
    });
  },
  threadCreated: (threadId: string) => {
    trackEvent('thread_created', { thread_id: threadId });
  },
};

/**
 * Track search events
 */
export const trackSearch = {
  performed: (searchTerm: string, resultsCount?: number) => {
    trackEvent('search', {
      search_term: searchTerm,
      results_count: resultsCount,
    });
  },
  noResults: (searchTerm: string) => {
    trackEvent('search_no_results', { search_term: searchTerm });
  },
};

/**
 * Track file operations
 */
export const trackFile = {
  uploaded: (fileType: string, fileSize: number) => {
    trackEvent('file_uploaded', {
      file_type: fileType,
      file_size: fileSize,
    });
  },
  downloaded: (fileType: string) => {
    trackEvent('file_downloaded', { file_type: fileType });
  },
  exported: (exportType: string, format: string) => {
    trackEvent('export_completed', {
      export_type: exportType,
      format: format,
    });
  },
};

/**
 * Track errors
 */
export const trackError = (errorMessage: string, errorLocation?: string) => {
  trackEvent('exception', {
    description: errorMessage,
    fatal: false,
    error_location: errorLocation,
  });
};

/**
 * Track feature usage
 */
export const trackFeature = {
  accessed: (featureName: string) => {
    trackEvent('feature_accessed', { feature_name: featureName });
    // Track feature adoption (first time user uses a feature)
    trackFeatureAdoption(featureName);
  },
  enabled: (featureName: string) => {
    trackEvent('feature_enabled', { feature_name: featureName });
  },
};

/**
 * Track feature adoption (first time user uses a feature)
 * Stores adopted features in localStorage and sets user property
 */
const trackFeatureAdoption = (featureName: string): void => {
  if (typeof window === 'undefined') return;

  try {
    const adoptedFeaturesKey = 'analytics_adopted_features';
    const adoptedStr = localStorage.getItem(adoptedFeaturesKey);
    const adoptedFeatures = adoptedStr ? new Set(JSON.parse(adoptedStr)) : new Set<string>();

    // Check if this is the first time user is using this feature
    if (!adoptedFeatures.has(featureName)) {
      adoptedFeatures.add(featureName);
      localStorage.setItem(adoptedFeaturesKey, JSON.stringify([...adoptedFeatures]));

      // Track feature adoption event
      trackEvent('feature_adopted', {
        feature_name: featureName,
        adoption_date: new Date().toISOString(),
      });

      // Update user property with adopted features count
      setUserProperties({
        features_adopted_count: adoptedFeatures.size,
        last_feature_adopted: featureName,
      });
    }
  } catch (error) {
    console.warn('Failed to track feature adoption:', error);
  }
};

/**
 * Get list of adopted features for current user
 */
export const getAdoptedFeatures = (): string[] => {
  if (typeof window === 'undefined') return [];

  try {
    const adoptedStr = localStorage.getItem('analytics_adopted_features');
    return adoptedStr ? JSON.parse(adoptedStr) : [];
  } catch (error) {
    return [];
  }
};

/**
 * Track engagement metrics (session duration, features used, etc.)
 * Should be called periodically or on important actions
 */
export const trackEngagement = () => {
  if (typeof window === 'undefined') return;

  try {
    // Import dynamically to avoid circular dependency
    import('./sessionTracker').then(({ getSessionDuration, getPageViewCount, getFeaturesUsed }) => {
      const duration = getSessionDuration();
      const pageViews = getPageViewCount();
      const featuresUsed = getFeaturesUsed();
      const adoptedFeatures = getAdoptedFeatures();

      trackEvent('engagement_metrics', {
        session_duration_seconds: duration,
        page_views: pageViews,
        features_used_count: featuresUsed.length,
        features_adopted_count: adoptedFeatures.length,
      });
    }).catch((error) => {
      console.warn('Failed to load sessionTracker:', error);
    });
  } catch (error) {
    console.warn('Failed to track engagement metrics:', error);
  }
};

// Extend Window interface for TypeScript
declare global {
  interface Window {
    gtag: (
      command: string,
      targetId: string | Date | Record<string, unknown>,
      config?: Record<string, unknown>
    ) => void;
    dataLayer: unknown[];
  }
}

